import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Calendar, AlertCircle, RefreshCw } from "lucide-react";
import type { Candidate, College, JobPosition } from "@/types/database";
import { formatDisplayDate, formatDisplayTime } from "@/utils/timezoneUtils";

interface AssessmentErrorScreenProps {
  candidate: Candidate;
  college: College;
  jobPosition: JobPosition | null;
  error: {
    errorCode?: string;
    error: string;
    availableTime?: string;
    availableDate?: string;
  };
  onRetry?: () => void;
  isRetrying?: boolean;
}

export function AssessmentErrorScreen({
  candidate,
  college,
  jobPosition,
  error,
  onRetry,
  isRetrying = false,
}: AssessmentErrorScreenProps) {
  const getErrorIcon = () => {
    switch (error.errorCode) {
      case "WRONG_DATE":
        return Calendar;
      case "TOO_EARLY":
      case "TOO_LATE":
        return Clock;
      default:
        return AlertCircle;
    }
  };

  const getErrorColor = () => {
    switch (error.errorCode) {
      case "TOO_LATE":
        return "text-red-500";
      case "WRONG_DATE":
      case "TOO_EARLY":
        return "text-orange-500";
      default:
        return "text-red-500";
    }
  };

  const getTitleColor = () => {
    switch (error.errorCode) {
      case "TOO_LATE":
        return "text-red-600";
      case "WRONG_DATE":
      case "TOO_EARLY":
        return "text-orange-600";
      default:
        return "text-red-600";
    }
  };

  const getTitle = () => {
    switch (error.errorCode) {
      case "WRONG_DATE":
        return "Assessment Not Available Today";
      case "TOO_EARLY":
        return "Assessment Not Started Yet";
      case "TOO_LATE":
        return "Assessment Window Closed";
      case "ALREADY_STARTED":
        return "Assessment Already Started";
      case "ALREADY_SUBMITTED":
        return "Assessment Already Submitted";
      case "SCHEDULE_NOT_CONFIGURED":
        return "Assessment Not Configured";
      default:
        return "Assessment Not Available";
    }
  };

  const showRetryButton = () => {
    return error.errorCode === "TOO_EARLY" || !error.errorCode;
  };

  const ErrorIcon = getErrorIcon();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <ErrorIcon className={`h-16 w-16 ${getErrorColor()}`} />
          </div>
          <CardTitle className={`text-2xl font-bold ${getTitleColor()}`}>
            {getTitle()}
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 mt-2">
            {error.error}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Candidate Information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <span className="ml-2 text-gray-900">{candidate.full_name}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">College:</span>
                <span className="ml-2 text-gray-900">{college.name}</span>
              </div>
              {jobPosition && (
                <div>
                  <span className="font-medium text-gray-700">Position:</span>
                  <span className="ml-2 text-gray-900">{jobPosition.role_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Assessment Schedule Information */}
          {(college.start_time && college.end_time) && (
            <div className="bg-gray-100 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Assessment Schedule (IST)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {college.college_visit_date && (
                  <div>
                    <span className="font-medium text-gray-700">Date:</span>
                    <span className="ml-2 text-gray-900">
                      {formatDisplayDate(college.college_visit_date)}
                    </span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Time:</span>
                  <span className="ml-2 text-gray-900">
                    {formatDisplayTime(college.start_time)} - {formatDisplayTime(college.end_time)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Duration:</span>
                  <span className="ml-2 text-gray-900">{college.duration_minutes} minutes</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                All times are in Indian Standard Time (IST)
              </p>
            </div>
          )}

          {/* Action Button */}
          {showRetryButton() && onRetry && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={onRetry}
                disabled={isRetrying}
                variant="outline"
                className="px-6 py-2"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Contact Information */}
          <div className="text-center text-sm text-gray-600">
            <p>
              If you believe this is an error, please contact your placement coordinator
              or our support team.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
